import React, { useState } from "react";
import { Row, Col, Form, Button, Dropdown } from "react-bootstrap";
import {
  AddReportTime,
  EditReportTime,
} from "../../../store/actions/dashboardsAction";
import { useDispatch, useSelector } from "react-redux";
import ApiResponsePopup from "../../common/modal/ApiResponsePopup";
import Loader from "../../common/loader/Loader";
import moment from "moment";
import { AddIMCTime, EditIMCTime } from "../../../store/actions/imcAction";

const daysOptions = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 0, label: "Sunday" },
];

export default function TimingsForm({ onSave = () => {}, editData = null }) {
  const [time, setTime] = useState(editData?.time || "");
  const [frequency, setFrequency] = useState(editData?.recur?.freq || "DAILY");
  const [weekDays, setWeekDays] = useState(
    editData?.recur?.byDay
      ? daysOptions.filter((d) => editData.recur.byDay.includes(d.value))
      : [],
  );
  const [monthDay, setMonthDay] = useState(
    editData?.recur?.byMonthDay?.[0] || "",
  );
  const [isShow, setIsShow] = useState(false);
  const [popupMessage, setPopupMessage] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();
  const { isLoader, error: apiError } = useSelector((state) => state.imcs);
  const { adminDetail } = useSelector((state) => state.admin);

  const hideHandlerRes = () => setIsShow(false);

  // 🌀 Frequency change handler
  const handleFrequencyChange = (e) => {
    const value = e.target.value.toUpperCase();
    setFrequency(value);
    if (value !== "WEEKLY") {
      setWeekDays([]);
    }
    if (value !== "MONTHLY" && value !== "QUARTERLY") {
      setMonthDay("");
    }
  };

  const [formError, setFormError] = useState("");

  // 🧩 Submit handler
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // ---------------------- VALIDATION ----------------------

    if (!time) {
      setFormError("Please select a time.");
      setIsSubmitting(false);
      return;
    }

    if (frequency === "WEEKLY" && weekDays.length === 0) {
      setFormError("Please select at least one weekday.");
      setIsSubmitting(false);
      return;
    }

    if ((frequency === "MONTHLY" || frequency === "QUARTERLY") && !monthDay) {
      setFormError("Please select a day of the month.");
      setIsSubmitting(false);
      return;
    }

    setFormError(""); // clear previous errors

    // ---------------------- CONTINUE SUBMIT ----------------------
    const [hour = "00", minute = "00"] = time ? time.split(":") : ["00", "00"];

    const payload = {
      hour,
      minute,
      recur: {
        freq: frequency === "QUARTERLY" ? "MONTHLY" : frequency,
        interval:
          frequency === "BIWEEKLY" ? 2 : frequency === "QUARTERLY" ? 3 : 1,
        byDay:
          frequency === "WEEKLY" || frequency === "BIWEEKLY" ? weekDays : [],
        byMonthDay:
          frequency === "MONTHLY" || frequency === "QUARTERLY"
            ? monthDay
              ? [Number(monthDay)]
              : []
            : [],
        anchorDate: moment().format("YYYY-MM-DD"),
      },
    };

    // 🧠 Add or Edit API Call
    if (editData?._id) {
      // ✏️ Edit existing record
      const newPayload = {
        timeId: editData._id,
        hour: payload.hour,
        minute: payload.minute,
        recur: payload.recur,
      };
      dispatch(EditIMCTime(newPayload)).then(({ payload }) => {
        setIsSubmitting(false);
        if (payload?.success) {
          //   setPopupMessage("Report time updated successfully!");
          setIsSuccess(true);
          resetForm();
        } else {
          setPopupMessage(apiError?.message || "Failed to update report time.");
          setIsSuccess(false);
        }
        setIsShow(true);
      });
    } else {
      // ➕ Add new record
      console.log("payload-120", payload);
      dispatch(AddIMCTime(payload)).then(({ payload }) => {
        setIsSubmitting(false);
        if (payload?.success) {
          //   setPopupMessage("Report time added successfully!");
          setIsSuccess(true);
          resetForm();
        } else {
          // setPopupMessage(apiError?.message || "Failed to add report time.");
          setIsSuccess(false);
        }
        setIsShow(true);
      });
    }
  };

  const resetForm = () => {
    setTime("");
    setFrequency("DAILY");
    setWeekDays([]);
    setMonthDay("");
  };

  // const formattedWeekDays = weekDays?.map((d) => ({
  //   value: d.value,
  //   label: d.label.slice(0, 3), // 👈 makes "Monday" → "Mon"
  // }));

  const toggleWeekDay = (value) => {
    setWeekDays((prev) =>
      prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value],
    );
  };

  return (
    <>
      {isLoader && <Loader />}
      <section style={{ padding: "0.5rem", width: "100%" }}>
        {formError && (
          <p className="text-danger mt-2" style={{ fontWeight: 500 }}>
            {formError}
          </p>
        )}
        <Form
          onSubmit={handleSubmit}
          style={{ width: "100%", padding: "10px 16px" }}
        >
          <Row className="align-items-end g-2 flex-nowrap">
            {/* Time */}
            <Col style={{ flex: "0 0 12%" }}>
              <Form.Group>
                <Form.Label>Time</Form.Label>
                <div style={{ position: "relative" }}>
                  <Form.Control
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    style={{
                      height: "38px",
                      paddingRight: "28px",
                    }}
                  />
                  {time && (
                    <button
                      type="button"
                      onClick={() => setTime("")}
                      style={{
                        position: "absolute",
                        right: "0px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        color: "#888",
                        fontSize: "16px",
                        lineHeight: "1",
                      }}
                      title="Clear time"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </Form.Group>
            </Col>

            {/* Frequency */}
            <Col style={{ flex: "0 0 12%" }}>
              <Form.Group>
                <Form.Label>Frequency</Form.Label>
                <Form.Select value={frequency} onChange={handleFrequencyChange}>
                  <option value="DAILY">Daily</option>
                  <option value="WEEKLY">Weekly</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Week Days */}
            <Col style={{ flex: "0 0 50%" }}>
              <Form.Group>
                <Form.Label>Week Days</Form.Label>

                <Dropdown className="w-100 border rounded">
                  <Dropdown.Toggle
                    variant="light"
                      className={`w-100 text-start ${
                      frequency === "DAILY" ||
                      frequency === "MONTHLY" ||
                      frequency === "QUARTERLY"
                        ? "custom-disabled-field"
                        : ""
                    }`}
                    disabled={
                      frequency === "DAILY" ||
                      frequency === "MONTHLY" ||
                      frequency === "QUARTERLY"
                    }
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "block",
                    }}
                  >
                    {weekDays?.length > 0
                      ? daysOptions
                          .filter((d) => weekDays.includes(d.value))
                          .map((d) => d.label)
                          .join(", ")
                      : "Select Week Days"}
                  </Dropdown.Toggle>

                  <Dropdown.Menu
                    className="w-100"
                    style={{
                      maxHeight: "240px",
                      overflowY: "auto",
                    }}
                  >
                    {daysOptions.map((day) => (
                      <Dropdown.Item
                        as="div"
                        key={day.value}
                        className="d-flex align-items-center"
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation(); // keep dropdown open
                          toggleWeekDay(day.value); // ✅ label / row click works
                        }}
                      >
                        <Form.Check
                          type="checkbox"
                          label={day.label}
                          checked={weekDays?.includes(day.value)}
                          onChange={() => {}} // controlled via parent
                          onClick={(e) => e.stopPropagation()} // prevent double toggle
                        />
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>
            </Col>

            {/* Month Day */}
            <Col style={{ flex: "0 0 12%" }}>
              <Form.Group>
                <Form.Label>Month Day</Form.Label>
                <Form.Select
                  value={monthDay}
                  onChange={(e) => setMonthDay(e.target.value)}
                  disabled={
                    frequency !== "MONTHLY" && frequency !== "QUARTERLY"
                  }
                >
                  <option value="">Select</option>
                  {[...Array(31)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Submit */}
            <Col style={{ flex: "0 0 auto" }}>
              <Button
                type="submit"
                variant="success"
                className="mt-3"
                style={{
                  height: "fit-content",
                  width: isSubmitting ? "auto" : "120px",
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Please wait..." : "Submit"}
              </Button>
            </Col>
          </Row>
        </Form>
      </section>

      {/* ✅ Popup */}
      {isShow && (
        <ApiResponsePopup
          hideHandlerRes={hideHandlerRes}
          isRecordAdded={isShow}
          title="Auto report for IMC/Stroke"
          successMsg={
            popupMessage ?? "Auto report for IMC/Stroke updated successfully"
          }
          errorRes={apiError?.message ?? popupMessage ?? null}
        />
      )}
    </>
  );
}
